import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast} from '@hm/fundamental'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import type {CommentDto} from '../api'
import {getCommentsPath} from '../api'

type Props = {
  plantId: string
  commentCategoryInstanceIds: string[]
  commentsCategory: string
  commentsSortOrder?: string
  commentsSortBy?: string
}

export const useComments = <TSelect = CommentDto[]>(
  {plantId, commentCategoryInstanceIds, commentsCategory, commentsSortOrder, commentsSortBy}: Props,
  queryKeyPrefix: string = 'comments',
  options?: Omit<UseQueryOptions<CommentDto[], AxiosError, TSelect>, 'queryKey' | 'queryFn'>
) => {
  const {t} = useTranslation()

  const query = useQuery<CommentDto[], AxiosError, TSelect>({
    queryKey: [
      queryKeyPrefix,
      plantId,
      commentsCategory,
      commentCategoryInstanceIds,
      commentsSortOrder,
      commentsSortBy
    ],
    queryFn: async ({signal}) => {
      const path = getCommentsPath({plantId, commentsCategory})
      const response = await ApiClient.axiosInstance.get<CommentDto[]>(path, {
        params: {
          commentCategoryInstanceIds: commentCategoryInstanceIds.join(','),
          ...(commentsSortOrder && {sortOrder: commentsSortOrder}),
          ...(commentsSortBy && {sortBy: commentsSortBy})
        },
        signal
      })

      return response.data
    },
    ...options
  })

  useEffect(() => {
    if (query.error) {
      spawnErrorToast(t('comments.message.failedToFetchComments'))
    }
  }, [query.error, t])

  return query
}
